import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  /** Identifies the wrapped region in logs and the fallback UI. */
  label?: string;
  /** Compact fallback for per-pane boundaries (no full-page chrome). */
  inline?: boolean;
};

type State = { error: Error | null };

/**
 * Catches render/lifecycle errors so a crash degrades to a fallback instead
 * of a white screen.  Never resets Replicache state — local data stays put;
 * a reload (or retry for inline boundaries) re-mounts the tree.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[error-boundary] ${this.props.label ?? "app"}`,
      error,
      info.componentStack,
    );
  }

  render() {
    if (!this.state.error) return this.props.children;

    if (this.props.inline) {
      return (
        <div className="flex h-full min-h-32 flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            {this.props.label
              ? `The ${this.props.label} view hit an error.`
              : "This view hit an error."}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Your data is safe — it lives on this device and syncs when
            possible.
          </p>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-slate-300"
          >
            Try again
          </button>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 p-8 text-center dark:bg-slate-900">
        <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Something went wrong
        </h1>
        <p className="max-w-sm text-sm text-slate-600 dark:text-slate-300">
          The app hit an unexpected error. Your data is safe — it lives on
          this device and syncs when possible.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-slate-300"
        >
          Reload
        </button>
      </div>
    );
  }
}
